import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { MutableRefObject } from "react";
import { RtfmAppState } from "./rtfmAppStateShared";
import { useRtfmAppState } from "./useRtfmAppState";
export type ExperiencePhase =
    | "initial"
    | "queue"
    | "preparing"
    | "active"
    | "ending"
    | "post";

export type RtfmLifecycle = {
    experiencePhase: ExperiencePhase;
    didStartExperience: boolean;
    didFinishExperience: boolean;
    sessionTotalSeconds: number;
    sessionRemainingSeconds: number;
    sessionStartedAt: number | null;
    sessionEndsAt: number | null;
}


//TODO: serious bug, didFinishExperience is never being set to true because the server closes the connection before the experience is finished on the frontend (two different 3 minute timers)
//we could solve this by making the server's session longer than the frontend by ~30 seconds but its midnight and we're about to launch
const ENV_SESSION_TOTAL_SECONDS = import.meta.env.VITE_SESSION_TOTAL_SECONDS ? parseInt(import.meta.env.VITE_SESSION_TOTAL_SECONDS) : undefined;
export const DEFAULT_SESSION_TOTAL_SECONDS = ENV_SESSION_TOTAL_SECONDS ?? 180; //3 minutes
export const QUEUE_STEP_SECONDS = 1;
export const BAD_CONNECTION_THRESHOLD_MS = 5000;
export const TIMER_WARNING_THRESHOLD = 30;
export const TIMER_CRITICAL_THRESHOLD = 15;
export const QUEUE_MIN_DISPLAY_MS = 2500;
export const PREPARING_TIMEOUT = 1000;
export const ENDING_TIMEOUT = 2000;

export const defaultLifecycle: RtfmLifecycle = {
    experiencePhase: "initial",
    didStartExperience: false,
    didFinishExperience: false,
    sessionTotalSeconds: DEFAULT_SESSION_TOTAL_SECONDS,
    sessionRemainingSeconds: DEFAULT_SESSION_TOTAL_SECONDS,
    sessionStartedAt: null,
    sessionEndsAt: null,
};

export type LifecycleAndAppState = RtfmLifecycle & RtfmAppState;

//takes the vanillaJS state and derived state and converts them into the lifecycle state
export function useRtfmLifecycle() {
    const { state } = useRtfmAppState();
    const [lifecycle, setLifecycleState] = useState<RtfmLifecycle>(defaultLifecycle);
    const setLifecycle = useCallback((updates: Partial<RtfmLifecycle>) => {
        setLifecycleState((prev: RtfmLifecycle) => ({ ...prev, ...updates }));
    }, []);
    const setDebugExperiencePhase = useCallback((experiencePhase: ExperiencePhase) => {
        setLifecycle({ experiencePhase });
    }, [setLifecycle]);

    const derived = useMemo(() => computeDerivedState(state, lifecycle), [state, lifecycle]);
    const lifecycleRef = useRef(lifecycle);

    useEffect(() => {
        lifecycleRef.current = lifecycle;
    }, [lifecycle]);

    const skipQueueing = import.meta.env.VITE_USE_GATEWAY !== "true" && import.meta.env.MODE === "development" && import.meta.env.VITE_FAKE_QUEUE !== "true";
    const queueStartRef = useRef<number | null>(null);
    const preparingTimeoutRef = useRef<number | null>(null);
    const sessionIntervalRef = useRef<number | null>(null);
    const endingTimeoutRef = useRef<number | null>(null);
    const sessionEndRef = useRef<number | null>(null);

    const clearTimeoutRef = (ref: MutableRefObject<number | null>) => {
        if (ref.current !== null) {
            window.clearTimeout(ref.current);
            ref.current = null;
        }
    };

    const clearIntervalRef = (ref: MutableRefObject<number | null>) => {
        if (ref.current !== null) {
            window.clearInterval(ref.current);
            ref.current = null;
        }
    };

    const applyLifecycleUpdate = useCallback((updates: Partial<RtfmLifecycle>) => {
        // console.log("applyLifecycleUpdate", updates);
        lifecycleRef.current = { ...lifecycleRef.current, ...updates };
        setLifecycle(updates);
    }, [setLifecycle]);

    const stopPreparingTimeout = useCallback(() => {
        clearTimeoutRef(preparingTimeoutRef);
    }, []);

    const stopSessionCountdown = useCallback(() => {
        clearIntervalRef(sessionIntervalRef);
    }, []);

    const stopEndingTimeout = useCallback(() => {
        clearTimeoutRef(endingTimeoutRef);
    }, []);

    //transition to POST means we are actually at the very very end
    const transitionToPost = useCallback(() => {
        if (lifecycleRef.current.experiencePhase === "post") {
            return;
        }
        stopEndingTimeout();
        applyLifecycleUpdate({
            experiencePhase: "post",
            sessionRemainingSeconds: 0,
        });
    }, [applyLifecycleUpdate, stopEndingTimeout]);

    //dont get it twisted, ending is the ANIMATION before we go to the end screen "post"
    const transitionToEnding = useCallback(() => {
        const currentPhase = lifecycleRef.current.experiencePhase;
        if (currentPhase === "ending" || currentPhase === "post") {
            return;
        }
        stopSessionCountdown();
        stopEndingTimeout();
        applyLifecycleUpdate({
            didFinishExperience: true,
            experiencePhase: "ending",
            sessionRemainingSeconds: 0,
        });
        endingTimeoutRef.current = window.setTimeout(() => {
            transitionToPost();
        }, ENDING_TIMEOUT);
    }, [applyLifecycleUpdate, stopEndingTimeout, stopSessionCountdown, transitionToPost]);

    const transitionToActive = useCallback(() => {
        if (lifecycleRef.current.experiencePhase === "active") {
            return;
        }
        stopPreparingTimeout();
        stopSessionCountdown();
        stopEndingTimeout();
        const totalSeconds = lifecycleRef.current.sessionTotalSeconds ?? DEFAULT_SESSION_TOTAL_SECONDS;
        const startedAt = Date.now();
        const endsAt = startedAt + totalSeconds * 1000;
        sessionEndRef.current = endsAt;
        applyLifecycleUpdate({
            didStartExperience: true,
            experiencePhase: "active",
            sessionStartedAt: startedAt,
            sessionEndsAt: endsAt,
            sessionRemainingSeconds: totalSeconds,
        });
        sessionIntervalRef.current = window.setInterval(() => {
            if (sessionEndRef.current === null) {
                return;
            }
            const now = Date.now();
            const remaining = Math.max(0, Math.ceil((sessionEndRef.current - now) / 1000));
            applyLifecycleUpdate({ sessionRemainingSeconds: remaining });
            if (remaining <= 0) {
                transitionToEnding();
            }
        }, 1000);
    }, [applyLifecycleUpdate, stopEndingTimeout, stopPreparingTimeout, stopSessionCountdown, transitionToEnding]);

    const transitionToPreparing = useCallback(() => {
        stopPreparingTimeout();
        stopSessionCountdown();
        stopEndingTimeout();
        queueStartRef.current = null;
        sessionEndRef.current = null;
        applyLifecycleUpdate({
            experiencePhase: "preparing",
            sessionStartedAt: null,
            sessionEndsAt: null,
            sessionRemainingSeconds: lifecycleRef.current.sessionTotalSeconds,
        });
        preparingTimeoutRef.current = window.setTimeout(() => {
            transitionToActive();
        }, PREPARING_TIMEOUT);
    }, [applyLifecycleUpdate, stopEndingTimeout, stopPreparingTimeout, stopSessionCountdown, transitionToActive]);

    const transitionToQueue = useCallback(() => {
        stopPreparingTimeout();
        stopSessionCountdown();
        stopEndingTimeout();
        sessionEndRef.current = null;
        queueStartRef.current = Date.now();
        applyLifecycleUpdate({
            experiencePhase: "queue",
            sessionStartedAt: null,
            sessionEndsAt: null,
            sessionRemainingSeconds: lifecycleRef.current.sessionTotalSeconds ?? DEFAULT_SESSION_TOTAL_SECONDS,
        });
    }, [applyLifecycleUpdate, stopEndingTimeout, stopPreparingTimeout, stopSessionCountdown]);

    //used to check if we have been allocated a spot or still queuing
    useEffect(() => {
        if (skipQueueing) {
            console.log("skipQueueing");
            if (lifecycleRef.current.experiencePhase !== "preparing") {
                transitionToPreparing();
            }
            return;
        }

        //we were put in the queue, lets transition to the queue experience state
        if (state.queueStatus === "queued") {
            if (queueStartRef.current === null) {
                queueStartRef.current = Date.now();
            }
            if (lifecycleRef.current.experiencePhase !== "queue") {
                transitionToQueue();
            }
        }

        //CONGRATS, we got allocated a GPU
        if (state.queueStatus === "allocated" || state.queueStatus === "active") {
            if(lifecycleRef.current.experiencePhase === "preparing") {
                console.log("already preparing");
                return;
            }

            if(lifecycleRef.current.experiencePhase !== "initial" && lifecycleRef.current.experiencePhase !== "queue") {
                console.warn("we aren't in queue")
                return;
            }

            transitionToPreparing();
            return;
        }

        //we got disconnected from the queue, lets transition direcetly to the post screen state ()
        if (state.queueStatus === "expired" || state.queueStatus === "not_found") {
            transitionToPost();
        }
    }, [skipQueueing, state.queueStatus, transitionToEnding, transitionToPreparing, transitionToQueue]);

    useEffect(() => {
        return () => {
            stopPreparingTimeout();
            stopSessionCountdown();
            stopEndingTimeout();
        };
    }, [stopEndingTimeout, stopPreparingTimeout, stopSessionCountdown]);

    return { lifecycle, derived, setLifecycle, setDebugExperiencePhase } as const;
}

//zustand store of lifecycle
// Zustand store removed in favor of local React state inside the hook


export interface RtfmDerivedState {
    isInitial: boolean;
    isQueueing: boolean;
    isPreparing: boolean;
    isActive: boolean;
    isEnding: boolean;
    isPost: boolean;
    queueLabel: string;
    timerLabel: string;
    timerVariant: "default" | "warning" | "critical";
    showQueueCard: boolean;
}

const computeDerivedState = (state: RtfmAppState, lifecycle: RtfmLifecycle): RtfmDerivedState => {
    const isInitial = lifecycle.experiencePhase === "initial";
    const isQueueing = lifecycle.experiencePhase === "queue";
    const isPreparing = lifecycle.experiencePhase === "preparing";
    const isActive = lifecycle.experiencePhase === "active";
    const isEnding = lifecycle.experiencePhase === "ending";
    const isPost = lifecycle.experiencePhase === "post";

    const queueLabel = state.queuePosition === -1
        ? "Requesting..."
        : state.queuePosition >= 0
            ? `Position ${state.queuePosition}`
            : "Ready";

    const timerVariant =
        lifecycle.sessionRemainingSeconds <= TIMER_CRITICAL_THRESHOLD
            ? "critical"
            : lifecycle.sessionRemainingSeconds <= TIMER_WARNING_THRESHOLD
                ? "warning"
                : "default";

    return {
        isInitial,
        isQueueing,
        isPreparing,
        isActive,
        isEnding,
        isPost,
        queueLabel,
        timerLabel: formatTimer(lifecycle.sessionRemainingSeconds),
        timerVariant,
        showQueueCard: lifecycle.experiencePhase === "queue" || lifecycle.experiencePhase === "post",
    } as RtfmDerivedState;
};

const formatTimer = (seconds: number) => {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(safeSeconds / 60);
  const remainder = safeSeconds % 60;
  return `${minutes}:${remainder.toString().padStart(2, "0")}`;
};

const formatEta = (seconds: number) => {
  const safeSeconds = Math.max(0, Math.round(seconds));
  if (safeSeconds === 0) {
    return "now";
  }
  if (safeSeconds < 60) {
    return `${safeSeconds}s`;
  }
  const minutes = Math.floor(safeSeconds / 60);
  const remainder = safeSeconds % 60;
  if (remainder === 0 || minutes >= 5) {
    return `${minutes}m`;
  }
  return `${minutes}m ${remainder}s`;
};
